import {
	ItemId,
	ItemId as BaseItemId,
	TreeData as BaseTreeData,
	TreeItem as BaseTreeItem,
} from "@atlaskit/tree/types";
import {
	moveItemOnTree as baseMoveItemOnTree,
	mutateTree as baseMutateTree,
} from "@atlaskit/tree";
// @ts-ignore
import { getItem, flattenTree } from "@atlaskit/tree/dist/esm/utils/tree";

export type TreeItemId = BaseItemId;

export type TreeItem<T = any> = BaseTreeItem & { data?: T };

export type Tree<T = any> = BaseTreeData & {
	items: Record<ItemId, TreeItem<T>>;
};

export type UniformTree<T = any> = Tree<T> & {
	childKey: string;
	idKey: string;
};

export const ROOT_ITEM_ID = "#root";

export const moveItemOnTree: typeof baseMoveItemOnTree = (tree, from, to) => {
	return {
		...tree,
		...baseMoveItemOnTree(tree, from, to),
	};
};

export const mutateTree: typeof baseMutateTree = (tree, itemId, mutation) => {
	return {
		...tree,
		...baseMutateTree(tree, itemId, mutation),
	};
};

export const createUniformTree = <
	T extends Record<K, T[] | undefined> & Record<I, string>,
	K extends string,
	I extends string,
>(
	items: T[],
	childKey: K,
	idKey: I,
): UniformTree<T> => {
	const treeItems: Record<string, TreeItem<T>> = {};
	const addNodes = (item: T, path: string | undefined = undefined) => {
		const id = item[idKey];
		const ownPath = `${path ? path + "." : ""}${id}`;

		let childItems: any = item[childKey];

		if (
			childItems &&
			typeof childItems === "object" &&
			!Array.isArray(childItems)
		) {
			childItems = Object.keys(item[childKey]).map(
				(groupKey: any) => item[childKey][groupKey],
			);
		}

		const children =
			childItems?.map((childItem: any) => addNodes(childItem, ownPath)) || [];
		const itemDepth = ownPath.split(".").length;
		treeItems[id] = {
			id: id,
			data: item,
			children: children,
			isExpanded: itemDepth < 5,
		};
		return id;
	};
	treeItems[ROOT_ITEM_ID] = {
		id: ROOT_ITEM_ID,
		children: items.map((item) => addNodes(item)),
	};
	return { rootId: ROOT_ITEM_ID, items: treeItems, childKey, idKey };
};

export interface TreeItemPositioned<T = any> extends TreeItem<T> {
	path: TreeItemId[];
}

export const findItem = <T>(
	tree: Tree<T>,
	predicate: (item: TreeItem<T>) => boolean,
): TreeItemPositioned<T>[] | undefined => {
	const foundItem = flattenTree(tree).find((flatItem: any) =>
		predicate(flatItem.item),
	);
	if (foundItem) {
		const items: TreeItemPositioned<T>[] = [];
		const path = [];
		const idPath: TreeItemId[] = [];
		for (let i = 0; i < foundItem.path.length; i++) {
			const pathPart = foundItem.path[i];
			path.push(pathPart);
			idPath.push(foundItem.item.id);
			items.push({ ...getItem(tree, path), path: idPath });
		}
		return items;
	}
	return undefined;
};

export const findItemPath = <T>(
	tree: Tree<T>,
	predicate: (item: TreeItem<T>) => boolean,
): TreeItemId[] | undefined => {
	const foundItems = findItem(tree, predicate);
	return foundItems ? foundItems[foundItems.length - 1].path : undefined;
};

export const unwrapUniformTree = <T>(tree: UniformTree<T>): T[] => {
	const items: T[] = [];
	const addGroup = (id: TreeItemId, parentGroups: T[]) => {
		const treeItem = tree.items[id];
		const item = tree.items[id].data;
		const children: T[] = [];
		for (const childId of treeItem.children) {
			addGroup(childId, children);
		}
		parentGroups.push({ ...item, [tree.childKey]: children });
	};

	for (const id of tree.items[ROOT_ITEM_ID].children) {
		addGroup(id, items);
	}
	return items;
};
